import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Tag, ToggleLeft, ToggleRight, RefreshCw, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface PromoCode {
  id: string;
  code: string;
  active: boolean;
  times_redeemed: number;
  max_redemptions: number | null;
  expires_at: string | null;
  created: string;
  coupon: {
    id: string;
    name: string;
    percent_off: number | null;
    amount_off: number | null;
    currency: string | null;
    duration: string;
    duration_in_months: number | null;
  };
}

export const PromoCodesManager = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [deletingPromo, setDeletingPromo] = useState<PromoCode | null>(null);
  const [saving, setSaving] = useState(false);

  const [formCode, setFormCode] = useState('');
  const [formType, setFormType] = useState<'percent' | 'amount'>('percent');
  const [formPercentOff, setFormPercentOff] = useState('100');
  const [formAmountOff, setFormAmountOff] = useState('');
  const [formDuration, setFormDuration] = useState('once');
  const [formDurationMonths, setFormDurationMonths] = useState('1');
  const [formMaxRedemptions, setFormMaxRedemptions] = useState('100');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [formCouponName, setFormCouponName] = useState('');

  const [editCouponName, setEditCouponName] = useState('');
  const [editMaxRedemptions, setEditMaxRedemptions] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('manage-promo-codes', {
        body: { action: 'list' },
      });
      if (error) throw error;
      setPromoCodes(data.promoCodes || []);
    } catch (err) {
      console.error('Erreur:', err);
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formCode.trim()) {
      toast.error('Le code est requis');
      return;
    }
    try {
      setCreating(true);
      const body: any = {
        action: 'create',
        code: formCode.trim(),
        duration: formDuration,
        couponName: formCouponName || `Promo ${formCode.trim()}`,
      };
      if (formType === 'percent') body.percentOff = parseFloat(formPercentOff);
      else body.amountOff = parseFloat(formAmountOff);
      if (formDuration === 'repeating') body.durationInMonths = parseInt(formDurationMonths);
      if (formMaxRedemptions) body.maxRedemptions = parseInt(formMaxRedemptions);
      if (formExpiresAt) body.expiresAt = formExpiresAt;

      const { data, error } = await supabase.functions.invoke('manage-promo-codes', { body });
      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast.success(`Code promo ${formCode.toUpperCase()} créé`);
      setShowCreate(false);
      resetForm();
      fetchPromoCodes();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (promoCode: PromoCode) => {
    try {
      const action = promoCode.active ? 'deactivate' : 'activate';
      const { data, error } = await supabase.functions.invoke('manage-promo-codes', {
        body: { action, promoCodeId: promoCode.id },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      toast.success(promoCode.active ? 'Code désactivé' : 'Code réactivé');
      fetchPromoCodes();
    } catch (err: any) {
      toast.error(err.message || 'Erreur');
    }
  };

  const openEdit = (pc: PromoCode) => {
    setEditingPromo(pc);
    setEditCouponName(pc.coupon.name);
    setEditMaxRedemptions(pc.max_redemptions?.toString() || '');
    setEditExpiresAt(pc.expires_at ? pc.expires_at.split('T')[0] : '');
  };

  const handleUpdate = async () => {
    if (!editingPromo) return;
    try {
      setSaving(true);
      const { data, error } = await supabase.functions.invoke('manage-promo-codes', {
        body: {
          action: 'update',
          promoCodeId: editingPromo.id,
          couponId: editingPromo.coupon.id,
          couponName: editCouponName,
          maxRedemptions: editMaxRedemptions ? parseInt(editMaxRedemptions) : undefined,
          expiresAt: editExpiresAt || undefined,
        },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      toast.success('Code promo mis à jour');
      setEditingPromo(null);
      fetchPromoCodes();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPromo) return;
    try {
      const promoId = deletingPromo.id;
      const promoCode = deletingPromo.code;
      const { data, error } = await supabase.functions.invoke('manage-promo-codes', {
        body: { action: 'delete', couponId: deletingPromo.coupon.id, promoCodeId: deletingPromo.id },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setPromoCodes((prev) => prev.filter((pc) => pc.id !== promoId));
      toast.success(`Code ${promoCode} supprimé`);
      setDeletingPromo(null);
      fetchPromoCodes();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormCode('');
    setFormType('percent');
    setFormPercentOff('100');
    setFormAmountOff('');
    setFormDuration('once');
    setFormDurationMonths('1');
    setFormMaxRedemptions('100');
    setFormExpiresAt('');
    setFormCouponName('');
  };

  const getDiscountLabel = (coupon: PromoCode['coupon']) => {
    if (coupon.percent_off) return `${coupon.percent_off}%`;
    if (coupon.amount_off) return `${(coupon.amount_off / 100).toFixed(2)} €`;
    return '-';
  };

  const getDurationLabel = (coupon: PromoCode['coupon']) => {
    switch (coupon.duration) {
      case 'once': return 'Une fois';
      case 'repeating': return `${coupon.duration_in_months} mois`;
      case 'forever': return 'Permanent';
      default: return coupon.duration;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">Chargement des codes promo...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Tag className="h-5 w-5" />
          Codes promotionnels ({promoCodes.length})
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPromoCodes}>
            <RefreshCw className="h-4 w-4 mr-1" /> Rafraîchir
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nouveau code
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Réduction</TableHead>
                <TableHead>Durée</TableHead>
                <TableHead>Utilisations</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promoCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Aucun code promo
                  </TableCell>
                </TableRow>
              ) : (
                promoCodes.map((pc) => (
                  <TableRow key={pc.id}>
                    <TableCell className="font-mono font-bold">{pc.code}</TableCell>
                    <TableCell className="text-sm">{pc.coupon.name}</TableCell>
                    <TableCell>{getDiscountLabel(pc.coupon)}</TableCell>
                    <TableCell>{getDurationLabel(pc.coupon)}</TableCell>
                    <TableCell>
                      {pc.times_redeemed}{pc.max_redemptions ? ` / ${pc.max_redemptions}` : ''}
                    </TableCell>
                    <TableCell>
                      {pc.expires_at
                        ? format(new Date(pc.expires_at), 'dd MMM yyyy', { locale: fr })
                        : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      {pc.active
                        ? <Badge variant="default">Actif</Badge>
                        : <Badge variant="secondary">Inactif</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(pc)} title={pc.active ? 'Désactiver' : 'Activer'}>
                          {pc.active
                            ? <ToggleRight className="h-5 w-5 text-primary" />
                            : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(pc)} title="Modifier">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeletingPromo(pc)} title="Supprimer" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un code promo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Code</Label>
              <Input value={formCode} onChange={(e) => setFormCode(e.target.value.toUpperCase())} placeholder="Ex: PROMO2025" className="uppercase bg-white" />
            </div>
            <div className="space-y-1">
              <Label>Nom du coupon</Label>
              <Input value={formCouponName} onChange={(e) => setFormCouponName(e.target.value)} placeholder="Ex: 1 mois offert" className="bg-white" />
            </div>
            <div className="space-y-1">
              <Label>Type de réduction</Label>
              <Select value={formType} onValueChange={(v) => setFormType(v as 'percent' | 'amount')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Pourcentage (%)</SelectItem>
                  <SelectItem value="amount">Montant fixe (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formType === 'percent' ? (
              <div className="space-y-1">
                <Label>Pourcentage de réduction</Label>
                <Input type="number" value={formPercentOff} onChange={(e) => setFormPercentOff(e.target.value)} min="1" max="100" className="bg-white" />
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Montant en euros</Label>
                <Input type="number" value={formAmountOff} onChange={(e) => setFormAmountOff(e.target.value)} min="0.5" step="0.5" className="bg-white" />
              </div>
            )}
            <div className="space-y-1">
              <Label>Durée de la réduction</Label>
              <Select value={formDuration} onValueChange={setFormDuration}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Une seule fois</SelectItem>
                  <SelectItem value="repeating">Plusieurs mois</SelectItem>
                  <SelectItem value="forever">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formDuration === 'repeating' && (
              <div className="space-y-1">
                <Label>Nombre de mois</Label>
                <Input type="number" value={formDurationMonths} onChange={(e) => setFormDurationMonths(e.target.value)} min="1" max="36" className="bg-white" />
              </div>
            )}
            <div className="space-y-1">
              <Label>Nombre max d'utilisations (vide = illimité)</Label>
              <Input type="number" value={formMaxRedemptions} onChange={(e) => setFormMaxRedemptions(e.target.value)} min="1" className="bg-white" />
            </div>
            <div className="space-y-1">
              <Label>Date d'expiration (optionnel)</Label>
              <Input type="date" value={formExpiresAt} onChange={(e) => setFormExpiresAt(e.target.value)} className="bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating}>{creating ? 'Création...' : 'Créer le code'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPromo} onOpenChange={(open) => !open && setEditingPromo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le code {editingPromo?.code}</DialogTitle>
          </DialogHeader>
          {editingPromo && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label>Nom du coupon</Label>
                <Input value={editCouponName} onChange={(e) => setEditCouponName(e.target.value)} className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label>Réduction</Label>
                <Input value={getDiscountLabel(editingPromo.coupon)} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">La réduction ne peut pas être modifiée dans Stripe. Supprimez et recréez le code si besoin.</p>
              </div>
              <div className="space-y-1">
                <Label>Nombre max d'utilisations (vide = illimité)</Label>
                <Input type="number" value={editMaxRedemptions} onChange={(e) => setEditMaxRedemptions(e.target.value)} min="1" className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label>Date d'expiration (vide = pas d'expiration)</Label>
                <Input type="date" value={editExpiresAt} onChange={(e) => setEditExpiresAt(e.target.value)} className="bg-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPromo(null)}>Annuler</Button>
            <Button onClick={handleUpdate} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingPromo} onOpenChange={(open) => !open && setDeletingPromo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le code {deletingPromo?.code} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le coupon et le code promotionnel seront définitivement supprimés de Stripe.
              {deletingPromo && deletingPromo.times_redeemed > 0 && (
                <span className="block mt-2 font-medium text-destructive">
                  Attention : ce code a déjà été utilisé {deletingPromo.times_redeemed} fois.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};